import React from 'react';
import { createPortal } from 'react-dom';
import ModalDocumentActionButtons from '../common/ModalDocumentActionButtons';
import type { AnnualCrossTopicCountScope } from '../../utils/annualProgressCrossTopicExtract';
import type { AnnualCrossTopicDetailRow } from '../../utils/annualProgressCrossTopicExtract';
import {
  openDocumentExportPreviewFailureMessage,
  openDocumentExportPreviewInNewTab,
  openDocumentExportPreviewTabPlaceholder,
} from '../../utils/documentExportPreviewStorage';
import { saveAnnualCrossTopicDetailAsExcel } from '../../utils/annualCrossTopicDetailExcelExport';
import LearningContentDetailTable from '../export/LearningContentDetailTable';

export type AnnualCrossTopicDetailModalProps = {
  open: boolean;
  onClose: () => void;
  topic: string;
  scope: AnnualCrossTopicCountScope;
  scopeLabel: string;
  rows: AnnualCrossTopicDetailRow[];
  loading?: boolean;
};

function sanitizeFileToken(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48);
}

const AnnualCrossTopicDetailModal: React.FC<AnnualCrossTopicDetailModalProps> = ({
  open,
  onClose,
  topic,
  scope,
  scopeLabel,
  rows,
  loading = false,
}) => {
  const printRootRef = React.useRef<HTMLDivElement | null>(null);
  const [exportBusy, setExportBusy] = React.useState(false);

  const canExport = !loading && rows.length > 0;

  const buildCrossTopicFileNameBase = React.useCallback(() => {
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    return `범교과주제_${sanitizeFileToken(scopeLabel)}_${sanitizeFileToken(topic)}_${dateLabel}`;
  }, [scopeLabel, topic]);

  const openCrossTopicPreviewTab = React.useCallback(
    (action: 'pdf' | 'print') => {
      if (!canExport) return;
      const placeholder = openDocumentExportPreviewTabPlaceholder();
      const result = openDocumentExportPreviewInNewTab(
        {
          v: 1,
          kind: 'annual-cross-topic-detail',
          fileNameBase: buildCrossTopicFileNameBase(),
          printTitle: `범교과 주제 프로그램 내용 — ${scopeLabel} ${topic}`,
          data: { topic, scope, scopeLabel, rows },
        },
        action,
        { preOpenedWindow: placeholder }
      );
      if (!result.ok) {
        alert(openDocumentExportPreviewFailureMessage(result.reason));
      }
    },
    [buildCrossTopicFileNameBase, canExport, rows, scope, scopeLabel, topic]
  );

  const handleSavePdf = React.useCallback(() => {
    openCrossTopicPreviewTab('pdf');
  }, [openCrossTopicPreviewTab]);

  const handleSaveExcel = React.useCallback(async () => {
    if (!canExport) return;
    setExportBusy(true);
    try {
      const fileName = `${buildCrossTopicFileNameBase()}.xlsx`;
      await saveAnnualCrossTopicDetailAsExcel({
        rows,
        scopeLabel,
        topic,
        fileName,
      });
    } catch (error) {
      console.error('엑셀 저장 실패:', error);
      alert('엑셀 저장에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [buildCrossTopicFileNameBase, canExport, rows, scopeLabel, topic]);

  const handlePrint = React.useCallback(() => {
    openCrossTopicPreviewTab('print');
  }, [openCrossTopicPreviewTab]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="cp-modal-backdrop cp-annual-cross-topic-detail-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="cp-modal cp-annual-cross-topic-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-annual-cross-topic-detail-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="cp-modal-document-header cp-annual-cross-topic-detail-header">
          <h3 id="cp-annual-cross-topic-detail-title">범교과 주제 프로그램 내용</h3>
          <ModalDocumentActionButtons
            onSavePdf={() => void handleSavePdf()}
            onSaveExcel={() => void handleSaveExcel()}
            onPrint={() => void handlePrint()}
            onClose={onClose}
            saveDisabled={!canExport}
            saveExcelDisabled={!canExport}
            printDisabled={!canExport}
            busy={exportBusy}
          />
        </div>
        <div className="cp-modal-body cp-annual-cross-topic-detail-body">
          {loading ? (
            <p className="page-desc" role="status" aria-busy="true">
              데이터 불러오는 중…
            </p>
          ) : rows.length === 0 ? (
            <p className="page-desc">표시할 프로그램 내용이 없습니다.</p>
          ) : (
            <div ref={printRootRef} className="cp-annual-cross-topic-detail-print-root">
              <div className="cp-annual-cross-topic-detail-subtitle" data-pdf-element="header">
                <span
                  className={`cp-annual-cross-topic-detail-scope cp-annual-cross-topic-detail-scope--${scope}`}
                >
                  {scopeLabel}
                </span>
                <span className="cp-annual-cross-topic-detail-topic" title={topic}>
                  {topic}
                </span>
              </div>
              <div className="cp-annual-cross-topic-detail-table-wrap">
                <LearningContentDetailTable rows={rows} />
              </div>
            </div>
          )}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AnnualCrossTopicDetailModal;
